import { Box, Drawer, DrawerProps } from '@mui/material';
import { ReactNode } from 'react';
import Logo from '../logo/Logo';

export const SIDEBAR_DRAWER_WIDTH = 240;
export const SIDEBAR_MINI_WIDTH = 96;

type MobileDrawerProps = {
  anchor?: DrawerProps['anchor'];
  zIndex?: number;
  paperStyles?: React.CSSProperties;
};

type DesktopDrawerProps = {
  paperStyles?: React.CSSProperties;
  containerStyles?: React.CSSProperties;
};

type SidebarProps = {
  openMobileMenu: boolean;
  setOpenMobileMenu: (open: boolean) => void;
  drawerWidth?: number;
  children: ReactNode;
  mobileDrawer?: MobileDrawerProps;
  desktopDrawer?: DesktopDrawerProps;
  logo?: ReactNode;
  /** Collapses the desktop sidebar to a narrow icon-above-text variant between the md and lg breakpoints. */
  miniOnNarrow?: boolean;
  /** Logo shown in the mini variant. Defaults to the logo icon without text. */
  miniLogo?: ReactNode;
  miniWidth?: number;
};

const Sidebar = ({
  openMobileMenu,
  setOpenMobileMenu,
  drawerWidth = SIDEBAR_DRAWER_WIDTH,
  children,
  mobileDrawer,
  desktopDrawer,
  logo,
  miniOnNarrow = false,
  miniLogo,
  miniWidth = SIDEBAR_MINI_WIDTH,
}: SidebarProps) => {
  const showFullOnly = miniOnNarrow
    ? { xs: 'none', md: 'none', lg: 'block' }
    : { xs: 'none', md: 'block' };
  const showMiniOnly = { xs: 'none', md: 'block', lg: 'none' };

  return (
    <Box>
      {/* mobile */}
      <Drawer
        open={openMobileMenu}
        anchor={mobileDrawer?.anchor ?? 'top'}
        onClose={() => setOpenMobileMenu(false)}
        ModalProps={{
          keepMounted: false,
          onClose: () => setOpenMobileMenu(false),
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: mobileDrawer?.zIndex ?? 999,
        }}
        variant='temporary'
        slotProps={{
          paper: {
            sx: {
              backgroundImage: 'none',
              minHeight: '100%',
              paddingTop: '96px',
              ...mobileDrawer?.paperStyles,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      </Drawer>

      {/* desktop */}
      <Drawer
        sx={(theme) => ({
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            left: 'unset',
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
            ...desktopDrawer?.paperStyles,
          },
          height: '100%',
          ...desktopDrawer?.containerStyles,
          ...(miniOnNarrow && {
            [theme.breakpoints.between('md', 'lg')]: {
              width: miniWidth,
              '& .MuiDrawer-paper': {
                width: miniWidth,
              },
              '& .SidebarMenuItem-root': {
                borderLeftWidth: 4,
              },
              '& .SidebarMenuItem-button': {
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.5,
                px: 0.5,
                py: 1.5,
              },
              '& .SidebarMenuItem-icon': {
                minWidth: 'auto',
                justifyContent: 'center',
              },
              '& .SidebarMenuItem-text': {
                m: 0,
                textAlign: 'center',
                '& .MuiTypography-root': {
                  fontSize: theme.typography.caption.fontSize,
                  lineHeight: 1.2,
                  overflowWrap: 'anywhere',
                },
              },
              '& .SidebarMenuItem-endIcon, & .SidebarMenuItem-expand, & .SidebarMenuItem-children':
                { display: 'none' },
            },
          }),
        })}
        variant='permanent'
      >
        <Box sx={{ display: showFullOnly }}>
          {logo ? (
            logo
          ) : (
            <Box sx={{ ml: 2, mb: 4, display: 'block' }}>
              <Logo size={60} sx={{ height: 35, mt: 2 }} />
            </Box>
          )}
        </Box>
        {miniOnNarrow && (
          <Box
            sx={{
              display: showMiniOnly,
              mb: 4,
              mt: 2,
              textAlign: 'center',
            }}
          >
            {miniLogo ?? <Logo withText={false} size={40} />}
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      </Drawer>
    </Box>
  );
};

export { Sidebar };
